import { Lock, Sparkles, ArrowRight, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Full-screen paywall gate rendered in place of the dashboard when a user's
 * 30-day free trial has expired and they haven't upgraded yet.
 *
 * The user's data is NEVER deleted here — the sites, images, business rows
 * and plans all stay in Mongo untouched. This is a UI-level access lock
 * only. Once the user completes checkout in `BillingTab`, `compute_plan_status`
 * flips `free_expired` back to false and the dashboard unlocks automatically.
 *
 * Props:
 *   - onUpgrade():  switches to the billing tab and shows the plan grid
 *   - onLogout():   sign out
 *   - userEmail:    for the "signed in as ..." line
 */
export default function TrialExpiredGate({ onUpgrade, onLogout, userEmail }) {
  return (
    <div
      className="min-h-screen bg-[#FFFDF0] grid place-items-center px-6"
      data-testid="trial-expired-gate"
    >
      <div className="max-w-xl w-full bg-white border-2 border-black rounded-2xl p-8 sm:p-10 nb-shadow-sm text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-100 border-2 border-black grid place-items-center mx-auto">
          <Lock className="w-6 h-6 text-emerald-700" strokeWidth={2} />
        </div>
        <div className="inline-flex items-center gap-2 mt-6 text-[11px] font-bold tracking-widest uppercase text-emerald-700 iti-chip px-3 py-1.5">
          <Sparkles className="w-3 h-3" /> Free trial ended
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-4 leading-tight">
          Upgrade to keep building.
        </h1>
        <p className="text-slate-600 mt-4 text-base leading-relaxed">
          Your 30-day free trial has ended. Upgrade to a paid plan to continue editing,
          managing, and publishing your website. Your website, images, business data,
          audience plan, and 30-day action plan are all safely saved and will be
          restored as soon as you upgrade.
        </p>

        <Button
          onClick={onUpgrade}
          data-testid="trial-upgrade-btn"
          className="iti-btn-primary mt-8 px-8 py-4 text-base font-semibold inline-flex items-center gap-2 w-full sm:w-auto"
        >
          Upgrade to a Paid Plan
          <ArrowRight className="w-4 h-4" />
        </Button>

        <div className="mt-8 pt-6 border-t border-slate-100 text-sm text-slate-500 space-y-2">
          <p className="text-xs">
            {userEmail && (
              <>Signed in as <span className="font-medium text-slate-800">{userEmail}</span> · </>
            )}
            <button
              onClick={onLogout}
              data-testid="trial-logout-btn"
              className="underline hover:text-slate-800 inline-flex items-center gap-1"
            >
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
